import { FC, useMemo } from 'react';

import { twMerge } from 'tailwind-merge';

import { AngleDown, AngleForward } from '@assets/icons';
import { Checkbox } from '@components/index';
import { KnowledgeAttribute } from '@store/atoms/knowledge-attributes';

interface ExpandableAttributeSectionProps {
  sectionIndex: number;
  // Non-nullable: the parent buckets unrecognised groups under a fallback.
  group: NonNullable<KnowledgeAttribute['attributeGroup']>;
  attributes: KnowledgeAttribute[];
  isExpanded: boolean;
  selectedAttributes: Set<number>;
  onToggleSection: (index: number) => void;
  onToggleAttribute: (attributeId: number) => void;
}

const ExpandableAttributeSection: FC<ExpandableAttributeSectionProps> = ({
  sectionIndex,
  group,
  attributes,
  isExpanded,
  selectedAttributes,
  onToggleSection,
  onToggleAttribute,
}) => {
  const totalCount = attributes.length;

  const selectedCount = useMemo(
    () => attributes.filter(attr => selectedAttributes.has(attr.id)).length,
    [attributes, selectedAttributes],
  );

  const selectionText = useMemo(() => {
    if (!totalCount) return 'No attributes';

    return selectedCount === totalCount
      ? 'All selected'
      : `${selectedCount} of ${totalCount} selected`;
  }, [selectedCount, totalCount]);

  return (
    <div className="border-b border-gray-2 last:border-b-0">
      <button
        type="button"
        onClick={() => onToggleSection(sectionIndex)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <AngleDown className="w-4 h-4 text-gray-9" />
          ) : (
            <AngleForward className="w-4 h-4 text-gray-9" />
          )}
          <div className="flex flex-col items-start">
            <span className="text-text-primary font-medium">{group.value}</span>
            {group.description && (
              <span className="text-text-secondary text-xs mt-1">
                {group.description}
              </span>
            )}
          </div>
        </div>
        <span
          className={twMerge(
            'text-sm',
            selectedCount === totalCount ? 'text-green-4' : 'text-black',
          )}
        >
          {selectionText}
        </span>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4">
          <div className="grid grid-cols-4 gap-4 mt-4">
            {attributes.map(({ id, displayName, description }) => {
              const isChecked = selectedAttributes.has(id);

              return (
                <div key={id} className="p-2">
                  <div className="flex items-start gap-2">
                    <Checkbox
                      checked={isChecked}
                      onChange={() => onToggleAttribute(id)}
                      className="mt-0.5"
                    />
                    <div className="flex flex-col flex-1">
                      <div className="font-medium text-text-primary text-sm">
                        {displayName}
                      </div>
                      <div className="text-xs text-text-secondary mt-1">
                        {description}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpandableAttributeSection;
